import axios, { AxiosRequestConfig, isAxiosError } from "axios"

import URLS from "~/constants/urls"
import useAgentOsStore from "~/store/agent-os"
import useGlobalStore from "~/store/global"

type RetryFetchOptions = RequestInit & {
  retries?: number
  retryDelay?: number
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

export const retryFetch = async (
  url: string,
  options: RetryFetchOptions = {}
): Promise<Response> => {
  const { retries = 3, retryDelay = 1000, ...fetchOptions } = options
  const method = fetchOptions.method?.toUpperCase() ?? "GET"

  const shouldRetry = method === "GET" || method === "POST"

  let lastError: Error | null = null

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const response = await fetch(url, fetchOptions)

      if (shouldRetry && response.status >= 500 && attempt < retries) {
        await sleep(retryDelay * Math.pow(2, attempt))
        continue
      }

      return response
    } catch (error) {
      lastError = error as Error

      if (shouldRetry && attempt < retries) {
        await sleep(retryDelay * Math.pow(2, attempt))
        continue
      }

      throw error
    }
  }

  throw lastError || new Error("Fetch failed after retries")
}

export const axiosInstance = axios.create({
  baseURL: URLS.V1,
  headers: {
    "Content-Type": "application/json"
  }
})

// PROTOTYPE prospectOS (RC3): endpoints whose 401 must NOT tear down the
// session. The outbound/prospect flow fans out many data calls — account
// people search, CRM pipeline recommendations, contact enrichment, and the 3s
// run poller. A single 401 from any of them used to clearStorage + POST
// /logout + redirect, logging the user out mid-flow (often correlated with
// long, high-latency runs where one call transiently 401s). Treat these as
// soft failures: reject so the caller handles it, but keep the session alive.
const SOFT_401_URL_FRAGMENTS = [
  "agent-workflows",
  "agent-os",
  "people_search",
  "peopleSearch",
  "additionalPeople",
  "email_enrichment",
  "inside_sales",
  "hot_accounts"
]

const is401SoftEndpoint = (url?: string) => {
  if (!url) return false
  return SOFT_401_URL_FRAGMENTS.some((frag) => url.includes(frag))
}

// Gate the soft-401 behaviour behind the prospectOS flag so NO other flow /
// non-prospectOS user is affected — for them, the original "any 401 ⇒ logout"
// behaviour is unchanged.
const prospectOsEnabled = () => {
  try {
    const ff = (useGlobalStore.getState() as any)?.user?.feature_flags
    return ff?.enable_prospectos === true || ff?.enable_prospectOS === true
  } catch {
    return false
  }
}

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (isAxiosError(error) && error.response?.status === 401) {
      const url = error.config?.url ?? ""
      // Per-request opt-out (config.skipAuthRedirect) OR — only when prospectOS
      // is enabled — a known prospectOS feature-data endpoint: fail soft
      // without destroying the session.
      const skipRedirect =
        (error.config as any)?.skipAuthRedirect === true ||
        (prospectOsEnabled() && is401SoftEndpoint(url))
      // Inside an iframe, never clobber the shared parent session.
      const inIframe =
        typeof window !== "undefined" && window.top !== window.self
      if (inIframe || skipRedirect) {
        return Promise.reject(error)
      }
      useAgentOsStore.persist.clearStorage()
      await fetch("/logout", { method: "POST" })
      window.location.href = "/login"
      return Promise.reject(error)
    }

    return Promise.reject(error)
  }
)

export const fetchDataOnServer = async (
  options: AxiosRequestConfig,
  request: Request
) => {
  try {
    const response = await axios({
      ...options,
      headers: {
        ...options.headers
      }
    })
    return response.data
  } catch (error) {
    if (isAxiosError(error)) {
      if (error.response?.status === 401) {
        try {
          const { logout } = await import("~/session")
          await logout(request)
        } catch {
          // do nothing
        }
        return {
          error: error.response.data?.error?.message,
          status: error.response?.status
        }
      } else if (error.response?.status === 400) {
        return {
          error: error.response.data?.error?.message,
          status: error.response?.status
        }
      } else {
        return {
          error: error.response?.data?.error?.message,
          status: error.response?.status
        }
      }
    }
    throw error
  }
}
